/** Data access for the dashboard. Money crosses the API as a decimal string (ADR-8). */

export type JsonFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

/** Wraps the authenticated fetch; the raw body is kept because the dashboard has no problem decoder. */
export function createDashboardFetch(
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>,
): JsonFetch {
  return async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const response = await authorizedFetch(path, init);
    if (!response.ok) {
      throw new Error(`${String(response.status)}: ${await response.text()}`);
    }
    return (await response.json()) as T;
  };
}

export interface Period {
  id: string;
  orgId: string;
  year: number;
  month: number;
  status: 'open' | 'closing' | 'closed';
}

export interface RecentDocument {
  id: string;
  type: string;
  number: string;
  date: string;
  status: 'draft' | 'pending' | 'approved' | 'posted' | 'cancelled';
  author: string;
  data: Record<string, unknown>;
}

export interface SideTotals {
  openingDt: string;
  openingCt: string;
  turnoverDt: string;
  turnoverCt: string;
  closingDt: string;
  closingCt: string;
}

export interface DashboardData {
  periods: Period[];
  documents: RecentDocument[];
  trial: { totals: SideTotals; offBalanceTotals: SideTotals };
}

export interface TrialHealth {
  turnover: string;
  debit: string;
  credit: string;
  difference: string;
  balanced: boolean;
}

const SCALE = 4;
const FACTOR = 10n ** BigInt(SCALE);

/** Parses `"1 234.5600"`, `"-2.5"` and `"0"` into an integer scaled by 10^4. */
export function parseMoney(value: string): bigint {
  const trimmed = value.trim().replace(/\s/gu, '').replace(',', '.');
  if (trimmed === '' || trimmed === '-') {
    return 0n;
  }
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const padded = (fraction + '0'.repeat(SCALE)).slice(0, SCALE);
  const digits = `${whole === '' ? '0' : whole}${padded}`.replace(/^0+(?=\d)/u, '');
  const scaled = BigInt(digits === '' ? '0' : digits);
  return negative ? -scaled : scaled;
}

function formatScaled(scaled: bigint): string {
  const negative = scaled < 0n;
  const absolute = negative ? -scaled : scaled;
  const whole = absolute / FACTOR;
  const fraction = absolute % FACTOR;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/gu, ' ');
  const cents = (fraction / 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${grouped},${cents}`;
}

export function formatMoney(value: string): string {
  return formatScaled(parseMoney(value));
}

/** Σ Dt vs Σ Kt over the period turnover, with the difference the health line shows. */
export function computeTrialHealth(totals: SideTotals): TrialHealth {
  const dt = parseMoney(totals.turnoverDt);
  const ct = parseMoney(totals.turnoverCt);
  const difference = dt > ct ? dt - ct : ct - dt;
  return {
    turnover: formatMoney(totals.turnoverDt),
    debit: formatMoney(totals.closingDt),
    credit: formatMoney(totals.closingCt),
    difference: formatScaled(difference),
    balanced: difference === 0n,
  };
}

export function pendingCount(documents: readonly RecentDocument[]): number {
  return documents.filter((document) => document.status === 'draft' || document.status === 'pending').length;
}

export function openPeriods(periods: readonly Period[]): Period[] {
  return periods.filter((period) => period.status !== 'closed');
}

export async function loadDashboard(fetchJson: JsonFetch): Promise<DashboardData> {
  const [periods, documents, trial] = await Promise.all([
    fetchJson<Period[]>('/periods'),
    fetchJson<RecentDocument[]>('/documents'),
    fetchJson<{ totals: SideTotals; offBalanceTotals: SideTotals }>('/reports/trial-balance'),
  ]);
  return {
    periods,
    documents: documents.slice(0, 10),
    trial: { totals: trial.totals, offBalanceTotals: trial.offBalanceTotals },
  };
}
